import {
	ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
	ATHENA_INGRESS_MOLLIE_SIGNATURE_INVALID,
	AthenaEventIngressError,
} from "../../../../../runtime/ingress/errors.ts";
import { deriveIngressProviderEventId } from "../../../../../runtime/ingress/identity.ts";
import type { AthenaIngressIR } from "../../../../../runtime/ingress/ir.ts";
import {
	type CanonicalBillingDocument,
	canonicalDocumentRevision,
	canonicalDocumentSubjectId,
} from "../../../../canonical/document.ts";
import { canonicalizeBillingEvents } from "../../../../canonical/transition.ts";
import type {
	BillingProviderWebhookPort,
	BillingWebhookVerificationStrategy,
} from "../webhook-port.ts";
import {
	classifyMollieWebhookBody,
	type MollieWebhookEnvelope,
	parseMollieWebhookBody,
} from "./parse-webhook.ts";
import { projectMollieResourceToCanonical } from "./project-document.ts";
import {
	MOLLIE_WEBHOOK_MAX_BODY_BYTES,
	verifyMollieWebhookSignature,
} from "./verify-webhook.ts";

export type MollieWebhookResource = {
	kind: "payment" | "subscription" | "invoice";
	raw: unknown;
};

export function mollieResourceKind(
	envelope: MollieWebhookEnvelope,
): "payment" | "subscription" | "invoice" {
	if (envelope.kind === "classic") {
		return "payment";
	}
	if (
		envelope.type.startsWith("subscription.") ||
		envelope.resource === "subscription"
	) {
		return "subscription";
	}
	if (
		envelope.type.startsWith("sales-invoice.") ||
		envelope.type.startsWith("invoice.") ||
		envelope.resource === "invoice"
	) {
		return "invoice";
	}
	return "payment";
}

export function mollieEnvelopeResourceId(
	envelope: MollieWebhookEnvelope,
): string {
	return envelope.kind === "classic" ? envelope.id : envelope.entityId;
}

function resolveVerification(input: {
	requireSignature?: boolean;
	resolveSigningSecrets?: (connectionId: string) => readonly string[];
	signingSecrets?: readonly string[];
	verification?: BillingWebhookVerificationStrategy;
}): BillingWebhookVerificationStrategy {
	if (input.verification) {
		return input.verification;
	}
	if (input.requireSignature === false) {
		return "authoritative_refetch";
	}
	if (
		input.requireSignature === true ||
		input.resolveSigningSecrets != null ||
		(input.signingSecrets && input.signingSecrets.length > 0)
	) {
		return "signature_and_refetch";
	}
	return "authoritative_refetch";
}

function signingSecretsForIngress(
	input: {
		resolveSigningSecrets?: (connectionId: string) => readonly string[];
		signingSecrets?: readonly string[];
	},
	connectionId: string | undefined,
): readonly string[] {
	if (input.resolveSigningSecrets) {
		if (connectionId == null || connectionId.length === 0) {
			return [];
		}
		return input.resolveSigningSecrets(connectionId);
	}
	return input.signingSecrets ?? [];
}

export function createMollieWebhookPort(input: {
	signingSecrets?: readonly string[];
	resolveSigningSecrets?: (connectionId: string) => readonly string[];
	requireSignature?: boolean;
	verification?: BillingWebhookVerificationStrategy;
	resolveResource: (input: {
		connectionId?: string;
		kind: "payment" | "subscription" | "invoice";
		id: string;
	}) => Promise<unknown>;
}): BillingProviderWebhookPort<MollieWebhookEnvelope, MollieWebhookResource> {
	const verification = resolveVerification(input);
	return {
		verification,
		parseIngress(ingress: AthenaIngressIR): MollieWebhookEnvelope {
			if (ingress.body.byteLength > MOLLIE_WEBHOOK_MAX_BODY_BYTES) {
				throw new AthenaEventIngressError({
					code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
					diagnostics: classifyMollieWebhookBody(
						ingress.body,
						ingress.headers["content-type"],
					),
					domain: "billing",
					message: "Mollie webhook payload exceeds size limit.",
					provider: "mollie",
					retryable: false,
				});
			}
			const eventsRoute = ingress.operation === "webhook.mollie.events";
			const classicRoute = ingress.operation === "webhook.mollie.classic";
			const contentType = ingress.headers["content-type"];
			const envelope = parseMollieWebhookBody(ingress.body, contentType);
			if (envelope.kind === "next_gen") {
				const requireSignature =
					eventsRoute ||
					(!classicRoute &&
						(verification === "signature" ||
							verification === "signature_and_refetch"));
				if (requireSignature) {
					const signature = ingress.headers["x-mollie-signature"];
					const signingSecrets = signingSecretsForIngress(input, ingress.connectionId);
					if (signingSecrets.length === 0) {
						throw new AthenaEventIngressError({
							code: ATHENA_INGRESS_MOLLIE_SIGNATURE_INVALID,
							domain: "billing",
							message:
								"Mollie next-gen webhook HMAC secret is not configured. Set billing.ingestion.webhooks.providers.mollie.nextGen.signingSecret to the webhook secret Mollie returned at create, or let automatic registration persist that secret.",
							provider: "mollie",
							retryable: false,
						});
					}
					if (
						!verifyMollieWebhookSignature({
							body: ingress.body,
							signatureHeader: signature,
							signingSecrets,
						})
					) {
						throw new AthenaEventIngressError({
							code: ATHENA_INGRESS_MOLLIE_SIGNATURE_INVALID,
							domain: "billing",
							message: "Mollie webhook signature is invalid.",
							provider: "mollie",
							retryable: false,
						});
					}
				}
			}
			if (classicRoute && envelope.kind !== "classic") {
				throw new AthenaEventIngressError({
					code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
					diagnostics: classifyMollieWebhookBody(ingress.body, contentType),
					domain: "billing",
					message: "Classic Mollie webhook expected a form id payload.",
					provider: "mollie",
					retryable: false,
				});
			}
			if (eventsRoute && envelope.kind !== "next_gen") {
				throw new AthenaEventIngressError({
					code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
					diagnostics: classifyMollieWebhookBody(ingress.body, contentType),
					domain: "billing",
					message: "Next-gen Mollie webhook expected a JSON event payload.",
					provider: "mollie",
					retryable: false,
				});
			}
			return envelope;
		},
		async resolveAuthoritativeState(envelope, ingress) {
			if (verification === "signature") {
				throw new AthenaEventIngressError({
					code: ATHENA_INGRESS_MOLLIE_SIGNATURE_INVALID,
					domain: "billing",
					message:
						"Mollie ingest requires authoritative refetch; signature-only is not supported.",
					provider: "mollie",
					retryable: false,
				});
			}
			const kind = mollieResourceKind(envelope);
			const id = mollieEnvelopeResourceId(envelope);
			const raw = await input.resolveResource({
				kind,
				id,
				...(ingress.connectionId
					? { connectionId: ingress.connectionId }
					: {}),
			});
			return { kind, raw };
		},
		projectDocument(resource) {
			return projectMollieResourceToCanonical(resource.raw, resource.kind);
		},
		canonicalizeEvents(context) {
			return canonicalizeBillingEvents(context);
		},
	};
}

export function mollieDeterministicEventId(input: {
	connectionId?: string;
	document: CanonicalBillingDocument;
}): string {
	const revision = canonicalDocumentRevision(input.document);
	return deriveIngressProviderEventId({
		provider: "mollie",
		connectionId: input.connectionId,
		resourceKind: input.document.kind,
		resourceId: canonicalDocumentSubjectId(input.document),
		authoritativeStateOrVersion: revision.sequence ?? input.document.status,
	});
}
